// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color } from "@vanmeter/XDL/Utilities";
import React, { createRef, Ref, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

export interface FancyScrollRefProps {
    updateScrollable: () => void;
}

export interface FancyScrollProps {
    children: React.ReactElement;
    refElement: React.RefObject<HTMLElement | null>;
    showArrow?: boolean;
    showIndicator?: boolean;
    extendedStyles?: InjectableCss;
}

export const ScrollIndicator = styled.span<
    { direction: "up" | "down"; transitionState: TransitionStatus } & ThemeProps<BaseTheme>
>`
    position: absolute;
    width: calc(100% - 5px);
    height: 26px;
    z-index: 1;
    color: ${getColor("common.white")};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all ease-in-out ${getProp("theme.transition.duration.regular")}ms;

    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
    ${({ direction }) => {
        if (direction === "up") {
            return css`
                top: 0;
                background: linear-gradient(
                    180deg,
                    ${color("common.white", 1)} 0%,
                    ${color("common.white", 0.6)} 60%,
                    ${color("common.white", 0)} 100%
                );
            `;
        }
        if (direction === "down") {
            return css`
                bottom: 0;
                background: linear-gradient(
                    0deg,
                    ${color("common.white", 1)} 0%,
                    ${color("common.white", 0.6)} 60%,
                    ${color("common.white", 0)} 100%
                );
            `;
        }
    }}
`;

const sum = (...a: number[]) => Math.ceil(a.reduce((previousValue, currentValue) => previousValue + currentValue, 0));

const FancyScroll = (
    { children, refElement, showArrow, showIndicator = true, extendedStyles }: FancyScrollProps,
    ref: Ref<{ updateScrollable: () => void }>,
) => {
    const [showScrollDown, setShowScrollDown] = useState(false);
    const [showScrollUp, setShowScrollUp] = useState(false);
    const scrollUpRef = createRef<any>();
    const scrollDownRef = createRef<any>();

    useImperativeHandle(ref, () => ({
        updateScrollable() {
            setTimeout(() => checkScrollable());
        },
    }));

    const checkScrollable = useCallback(() => {
        if (refElement.current && showIndicator) {
            if (
                refElement.current.scrollHeight > refElement.current.offsetHeight &&
                refElement.current.scrollTop > 20
            ) {
                setShowScrollUp(true);
            } else {
                setShowScrollUp(false);
            }
            if (
                refElement.current.scrollHeight - sum(refElement.current.offsetHeight + refElement.current.scrollTop) >
                1
            ) {
                setShowScrollDown(true);
            } else {
                setShowScrollDown(false);
            }
        }
    }, [refElement]);

    useEffect(() => {
        if (refElement && refElement.current) {
            setTimeout(() => checkScrollable());
            refElement.current.addEventListener("scroll", checkScrollable);
        }
        return () => {
            if (refElement && refElement.current) {
                refElement.current.removeEventListener("scroll", checkScrollable);
            }
        };
    }, [refElement]);

    return (
        <StyledWrapper
            css={css`
                position: relative;
                ${extendedStyles?.css}
            `}
            className="fancy-scroll"
        >
            {showIndicator && (
                <Transition
                    mountOnEnter
                    unmountOnExit
                    in={showScrollUp}
                    timeout={{ enter: 75, exit: 250 }}
                    nodeRef={scrollUpRef}
                >
                    {state => (
                        <ScrollIndicator ref={scrollUpRef} direction="up" transitionState={state}>
                            {showArrow && <IconMemo src={ChevronUp} size={14} />}
                        </ScrollIndicator>
                    )}
                </Transition>
            )}
            {children}
            {showIndicator && (
                <Transition
                    mountOnEnter
                    unmountOnExit
                    in={showScrollDown}
                    timeout={{ enter: 75, exit: 250 }}
                    nodeRef={scrollDownRef}
                >
                    {state => (
                        <ScrollIndicator ref={scrollDownRef} direction="down" transitionState={state}>
                            {showArrow && <IconMemo src={ChevronDown} size={14} />}
                        </ScrollIndicator>
                    )}
                </Transition>
            )}
        </StyledWrapper>
    );
};

export default React.forwardRef(FancyScroll);
